import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api } from "./client";
import { useEventStream } from "./eventStreamContext";
import { getPageActivity, usePageActivity } from "./pageActivity";
import {
  RuntimeStatusContext,
  type RuntimeStatusValue,
  type StatusFreshness,
} from "./runtimeStatusContext";
import { useResource } from "./useResource";
import type { Status } from "../types";

const STATUS_REFRESH_MS = 15_000;
const STATUS_REQUEST_DEADLINE_MS = 10_000;
const MAX_STATUS_AGE_MS = 30_000;
const RECOVERY_GRACE_MS = 25_000;
// One reconnect round trip plus one failed stream attempt retried after ~2.5 s fits inside it.
const RECOVERY_QUIET_MS = 5_000;

interface StatusSample {
  status: Status;
  receivedAt: number;
  ageAtReceipt: number | null;
  activation: number;
}

async function loadStatus(
  signal: AbortSignal,
  previous: StatusSample | null,
  activation: number,
): Promise<StatusSample> {
  const startedAt = performance.now();
  const request = new AbortController();
  const abortRequest = () => {
    request.abort(signal.reason);
  };
  if (signal.aborted) abortRequest();
  else signal.addEventListener("abort", abortRequest, { once: true });

  const deadline = window.setTimeout(() => {
    request.abort(new DOMException("Status request timed out", "TimeoutError"));
  }, STATUS_REQUEST_DEADLINE_MS);

  try {
    let serverNow = Number.NaN;
    const status = await api<Status>("/status", {
      signal: request.signal,
      cache: "no-store",
      onResponse: (response) => {
        const time = response.headers.get("x-auto-pools-time");
        serverNow = time !== null && /^\d{13}$/.test(time) ? Number(time) : Date.now();
      },
    });
    const receivedAt = performance.now();
    const updatedAt = Date.parse(status.panel.updatedAt);
    let ageAtReceipt =
      Number.isFinite(updatedAt) && updatedAt <= serverNow
        ? serverNow - updatedAt + Math.max(0, receivedAt - startedAt)
        : null;
    // Replayed snapshots cannot acquire fresh authority merely by being received again.
    if (previous && updatedAt <= Date.parse(previous.status.panel.updatedAt)) {
      ageAtReceipt =
        ageAtReceipt === null || previous.ageAtReceipt === null
          ? null
          : Math.max(
              ageAtReceipt,
              previous.ageAtReceipt + Math.max(0, receivedAt - previous.receivedAt),
            );
    }
    return { status, receivedAt, ageAtReceipt, activation };
  } catch (reason) {
    if (request.signal.aborted && !signal.aborted) {
      throw new Error("Server status did not respond in time");
    }
    throw reason;
  } finally {
    window.clearTimeout(deadline);
    signal.removeEventListener("abort", abortRequest);
  }
}

function statusFreshness(
  status: ReturnType<typeof useResource<StatusSample>>,
  now: number,
  active: boolean,
  activation: number,
): Pick<RuntimeStatusValue, "freshness" | "observedAgeMs" | "executionReady"> {
  const sample = status.data;
  const observedAgeMs =
    sample && sample.ageAtReceipt !== null
      ? sample.ageAtReceipt + Math.max(0, now - sample.receivedAt)
      : null;
  let freshness: StatusFreshness = "unknown";
  if (
    active &&
    sample?.activation === activation &&
    status.error === null &&
    observedAgeMs !== null
  ) {
    freshness = observedAgeMs <= MAX_STATUS_AGE_MS ? "fresh" : "stale";
  }
  return {
    freshness,
    observedAgeMs,
    executionReady:
      freshness === "fresh" &&
      status.data?.status.executor.state === "ready" &&
      !status.data.status.recoveryRequired,
  };
}

export function RuntimeStatusProvider({
  refresh = 0,
  children,
}: {
  /** Forces a fresh read from a caller; server ticks arrive through the status event below. */
  refresh?: number;
  children: ReactNode;
}) {
  const activity = usePageActivity();
  const stream = useEventStream();
  const previous = useRef<StatusSample | null>(null);
  const status = useResource<StatusSample>(
    `status:${String(refresh)}`,
    async (signal) => {
      const sample = await loadStatus(signal, previous.current, getPageActivity().activation);
      if (!signal.aborted) previous.current = sample;
      return sample;
    },
    STATUS_REFRESH_MS,
  );
  const reloadStatus = status.reload;
  useEffect(() => {
    // Runtime status follows only its own event. Reloading it from every named event in a tick
    // is what turned one server tick into a second full round of requests across the panel.
    const onServerEvent = (event: Event) => {
      if ((event as CustomEvent<unknown>).detail === "status") reloadStatus();
    };
    window.addEventListener("auto-pools:server-event", onServerEvent);
    return () => {
      window.removeEventListener("auto-pools:server-event", onServerEvent);
    };
  }, [reloadStatus]);
  const [now, setNow] = useState(() => performance.now());
  const currentTime = Math.max(now, performance.now());
  const episode = useRef({
    activation: activity.activation,
    deadline: activity.active ? currentTime + RECOVERY_GRACE_MS : null,
    quietUntil: activity.active ? currentTime + RECOVERY_QUIET_MS : null,
    recovered: false,
  });
  if (episode.current.activation !== activity.activation) {
    const restart = episode.current.recovered || episode.current.deadline === null;
    // Flapping keeps both deadlines, so quick returns can never stretch the quiet window either.
    episode.current = {
      activation: activity.activation,
      deadline: restart ? currentTime + RECOVERY_GRACE_MS : episode.current.deadline,
      quietUntil: restart ? currentTime + RECOVERY_QUIET_MS : episode.current.quietUntil,
      recovered: false,
    };
  }

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(performance.now());
    }, 1_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const authority = statusFreshness(status, currentTime, activity.active, activity.activation);
  const httpCurrent = authority.freshness === "fresh";
  const streamCurrent =
    stream.state === "connected" && stream.eventActivation === activity.activation;
  if (activity.active && httpCurrent && streamCurrent) {
    episode.current.recovered = true;
  }
  const recoveryPending = activity.active && (!httpCurrent || !streamCurrent);
  const recoveryGrace =
    recoveryPending &&
    !episode.current.recovered &&
    episode.current.deadline !== null &&
    currentTime < episode.current.deadline;
  const recoveryQuiet =
    recoveryGrace &&
    episode.current.quietUntil !== null &&
    currentTime < episode.current.quietUntil;
  const value = useMemo<RuntimeStatusValue>(
    () => ({
      ...status,
      data: status.data?.status ?? null,
      ...authority,
      recoveryPending,
      recoveryGrace,
      recoveryQuiet,
      active: activity.active,
    }),
    [activity.active, authority, recoveryGrace, recoveryPending, recoveryQuiet, status],
  );
  return <RuntimeStatusContext.Provider value={value}>{children}</RuntimeStatusContext.Provider>;
}
